import { AfterViewInit, Component, NgZone } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BaseComponent } from './base.component';
import { startUserWayMobilePositionWatcher } from './utils/userway-mobile-position';
import * as CookieConsent from 'vanilla-cookieconsent';

declare const Modernizr: any;

const DEBUG = false; // Set to true to enable tracking debugging logs

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-PQ3NB42JXP';

// Helper to get root domain for cookie deletion (e.g., 'www.example.com' -> '.example.com')
function getRootDomain(): string {
  const hostname = location.hostname;
  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }
  // Extract root domain (last two parts for standard TLDs)
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return hostname;
}

// Delete a cookie by name, trying multiple domain/path combinations
function deleteCookie(name: string): void {
  const domains = [location.hostname, getRootDomain(), ''];
  const paths = ['/', ''];
  
  domains.forEach(domain => {
    paths.forEach(path => {
      const domainPart = domain ? `; domain=${domain}` : '';
      const pathPart = path ? `; path=${path}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}${pathPart}`;
    });
  });
}

// Clear tracking cookies that shouldn't exist when consent is denied
function clearTrackingCookies(category: 'analytics' | 'marketing'): void {
  const analyticsCookiePatterns = [
    /^_ga/, /^_gid/, /^_gat/,           // Google Analytics
    /^_fbp$/, /^_fbc$/,                  // Facebook
    /^_ttp$/, /^_tt/, /^ttcsid/,         // TikTok
    /^_scid/, /^is_unique$/,             // StatCounter
    /^__qca$/,                           // Quantcast
  ];
  
  const marketingCookiePatterns = [
    /^_gcl/,                             // Google Ads
    /^IDE$/, /^NID$/,                    // DoubleClick
    /^_uetvid/,                          // Bing Ads
    /^_ttp$/, /^_tt/, /^ttcsid/,         // TikTok (also used for ads)
    /^__qca$/,                           // Quantcast
  ];
  
  const patterns = category === 'analytics' ? analyticsCookiePatterns : marketingCookiePatterns;
  
  // Get all cookies and check against patterns
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    patterns.forEach(pattern => {
      if (pattern.test(name)) {
        deleteCookie(name);
        if (DEBUG) { console.log(`[Cookie] Deleted ${name} (${category} consent denied)`); }
      }
    });
  });
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit {
  title = 'cms-frontend';
  supportsIE = false;
  private facebookConsentGranted = false;
  private gaReady = false;
  private umamiReady = false;

  constructor(public baseComponent: BaseComponent,
              private router: Router,
              private ngZone: NgZone) {
    
    if ( Modernizr.smil ) {
      this.supportsIE = true;
    } else {
      this.supportsIE = false;
    }

    // Wait for analytics scripts to load before tracking
    this.waitForAnalyticsScripts();

    // Track SPA page views using Router events
    // - GA: Uses gtag() directly. Consent Mode v2 handles cookieless pings when denied,
    //       and full tracking when consent is granted. No need to check consent here.
    // - Facebook: Only tracks after consent is granted (checked via facebookConsentGranted flag)
    // - Umami: Privacy-focused, always tracks (waits for script to load)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const pageUrl = event.urlAfterRedirects;
      const pageTitle = document.title;

      if (DEBUG) { console.log('[Tracking] NavigationEnd:', pageUrl); }

      // Track page view with Google Analytics (gtag.js)
      // gtag() is always available (defined inline) and queues to dataLayer
      // gtag.js processes dataLayer when it loads, so we can always call gtag()
      // But we check gaReady flag to ensure we're tracking (set once gtag/dataLayer confirmed)
      const gtag = (window as any).gtag;
      if (this.gaReady && typeof gtag === 'function') {
        try {
          gtag('config', GA_MEASUREMENT_ID, {
            'page_path': pageUrl,
            'page_title': pageTitle
          });
          if (DEBUG) { console.log('[GA] Page view sent:', pageUrl); }
        } catch (e) {
          if (DEBUG) { console.error('[GA] Error tracking page view:', e); }
        }
      } else if (!this.gaReady) {
        if (DEBUG) { console.log('[GA] Script not ready yet, skipping page view'); }
      }

      // Track page view with Facebook Pixel (only if consent granted)
      // The Facebook script is unblocked by vanilla-cookieconsent when analytics is accepted
      if (this.facebookConsentGranted && (window as any).fbq) {
        try {
          (window as any).fbq('track', 'PageView');
          if (DEBUG) { console.log('[FB] Page view sent:', pageUrl); }
        } catch (e) {
          if (DEBUG) { console.error('[FB] Error tracking page view:', e); }
        }
      }

      // Track page view with Umami (privacy-focused, always tracks)
      // Only track if Umami script has loaded
      if (this.umamiReady && (window as any).umami) {
        try {
          (window as any).umami.track(props => ({ ...props, url: pageUrl }));
          if (DEBUG) { console.log('[Umami] Page view sent:', pageUrl); }
        } catch (e) {
          if (DEBUG) { console.error('[Umami] Error tracking page view:', e); }
        }
      } else {
        if (DEBUG) { console.log('[Umami] Script not ready yet, skipping page view'); }
      }
    });
  }

  private waitForAnalyticsScripts(): void {
    // Check if scripts are already loaded
    this.checkGAScript();
    this.checkUmamiScript();

    // Poll for scripts to load (they should load quickly, but we'll check for up to 5 seconds)
    const checkInterval = setInterval(() => {
      if (this.gaReady && this.umamiReady) {
        clearInterval(checkInterval);
        if (DEBUG) { console.log('[Tracking] All analytics scripts loaded'); }
      } else {
        this.checkGAScript();
        this.checkUmamiScript();
      }
    }, 100);

    // Stop checking after 5 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (DEBUG) {
        console.log('[Tracking] Script check timeout - GA ready:', this.gaReady, 'Umami ready:', this.umamiReady);
      }
    }, 5000);
  }

  private checkGAScript(): boolean {
    if (this.gaReady) {
      return true;
    }

    // Check if gtag.js is ready
    // The gtag() function is always available (defined inline in index.html) and queues to dataLayer
    // gtag.js script processes dataLayer when it loads, so gtag() always works
    // We just need to verify gtag() and dataLayer exist (they should always exist)
    const gtag = (window as any).gtag;
    const dataLayer = (window as any).dataLayer;

    if (typeof gtag === 'function' && dataLayer && Array.isArray(dataLayer)) {
      this.gaReady = true;
      if (DEBUG) { console.log('[GA] Script confirmed ready (gtag() and dataLayer available)'); }
      return true;
    }
    return false;
  }

  private checkUmamiScript(): boolean {
    if (this.umamiReady) {
      return true;
    }

    // Check if Umami script has loaded
    if ((window as any).umami && typeof (window as any).umami.track === 'function') {
      this.umamiReady = true;
      if (DEBUG) { console.log('[Umami] Script confirmed ready'); }
      return true;
    }
    return false;
  }

  async ngAfterViewInit(): Promise<void> {
    await CookieConsent.run({
      onConsent: () => {
        // Update consent mode when user grants/denies consent
        // No page reload needed with Consent Mode v2
        const hasAnalyticsConsent = CookieConsent.acceptedCategory('analytics');
        const hasMarketingConsent = CookieConsent.acceptedCategory('marketing');
        const gtag = (window as any).gtag;

        // Update Google Consent Mode
        if (gtag) {
          gtag('consent', 'update', {
            'ad_storage': hasMarketingConsent ? 'granted' : 'denied',
            'ad_user_data': hasMarketingConsent ? 'granted' : 'denied',
            'ad_personalization': hasMarketingConsent ? 'granted' : 'denied',
            'analytics_storage': hasAnalyticsConsent ? 'granted' : 'denied'
          });
          if (DEBUG) { console.log('[GA] Consent updated - analytics:', hasAnalyticsConsent, 'marketing:', hasMarketingConsent); }
        }

        // Clear any leftover tracking cookies if consent is denied
        // This handles the case where cookies were set previously but consent is now denied
        if (!hasAnalyticsConsent) {
          clearTrackingCookies('analytics');
        }
        if (!hasMarketingConsent) {
          clearTrackingCookies('marketing');
        }

        // Enable Facebook page view tracking after consent is granted
        // The Facebook Pixel script is unblocked by vanilla-cookieconsent when analytics category is accepted
        // After unblocking, fbq() becomes available and we can track page views on navigation
        if (hasAnalyticsConsent && !this.facebookConsentGranted) {
          this.facebookConsentGranted = true;
          // Track the current page view immediately since the consent modal may have blocked it
          if ((window as any).fbq) {
            (window as any).fbq('track', 'PageView');
            if (DEBUG) { console.log('[FB] Initial page view sent after consent'); }
          }
          if (DEBUG) { console.log('[FB] Facebook tracking enabled after consent'); }
        }

        if (CookieConsent.acceptedCategory('functionality')) {
          startUserWayMobilePositionWatcher(this.ngZone);
        }
      },
      onChange: ({ changedCategories }) => {
        // Handle consent changes (e.g., user revokes consent via preferences modal)
        if (changedCategories.includes('analytics')) {
          const hasAnalyticsConsent = CookieConsent.acceptedCategory('analytics');
          if (!hasAnalyticsConsent) {
            // Disable Facebook tracking when analytics consent is revoked
            this.facebookConsentGranted = false;
            if (DEBUG) { console.log('[FB] Facebook tracking disabled - consent revoked'); }
          }
        }
        
        // Update Google consent mode on changes
        const hasAnalyticsConsent = CookieConsent.acceptedCategory('analytics');
        const hasMarketingConsent = CookieConsent.acceptedCategory('marketing');
        const gtag = (window as any).gtag;
        
        if (gtag) {
          gtag('consent', 'update', {
            'ad_storage': hasMarketingConsent ? 'granted' : 'denied',
            'ad_user_data': hasMarketingConsent ? 'granted' : 'denied',
            'ad_personalization': hasMarketingConsent ? 'granted' : 'denied',
            'analytics_storage': hasAnalyticsConsent ? 'granted' : 'denied'
          });
          if (DEBUG) { console.log('[GA] Consent updated on change - analytics:', hasAnalyticsConsent, 'marketing:', hasMarketingConsent); }
        }

        if (changedCategories.includes('functionality') && CookieConsent.acceptedCategory('functionality')) {
          startUserWayMobilePositionWatcher(this.ngZone);
        }
      },
      guiOptions: {
        consentModal: {
          layout: 'box wide',
          position: 'bottom left',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },
      categories: {
        necessary: {
          readOnly: true
        },
        functionality: {
          // UserWay doesn't set persistent cookies by default
        },
        analytics: {
          autoClear: {
            cookies: [
              // Google Analytics (try both hostname and root domain)
              { name: /^_ga/, path: '/', domain: location.hostname },
              { name: /^_ga/, path: '/', domain: getRootDomain() },
              { name: '_gid', path: '/', domain: location.hostname },
              { name: '_gid', path: '/', domain: getRootDomain() },
              { name: /^_gat/, path: '/', domain: location.hostname },
              // Facebook Pixel
              { name: '_fbp', path: '/', domain: location.hostname },
              { name: '_fbp', path: '/', domain: getRootDomain() },
              { name: '_fbc', path: '/', domain: location.hostname },
              { name: '_fbc', path: '/', domain: getRootDomain() },
              // TikTok Pixel
              { name: '_ttp', path: '/', domain: location.hostname },
              { name: '_ttp', path: '/', domain: getRootDomain() },
              { name: '_tt_enable_cookie', path: '/', domain: location.hostname },
              { name: '_tt_enable_cookie', path: '/', domain: getRootDomain() },
              { name: /^_tt/, path: '/', domain: location.hostname },
              { name: /^_tt/, path: '/', domain: getRootDomain() },
              { name: /^ttcsid/, path: '/', domain: location.hostname },
              { name: /^ttcsid/, path: '/', domain: getRootDomain() },
              // Quantcast
              { name: '__qca', path: '/', domain: location.hostname },
              { name: '__qca', path: '/', domain: getRootDomain() },
              // StatCounter
              { name: /^_scid/, path: '/', domain: location.hostname },
              { name: 'is_unique', path: '/', domain: location.hostname },
            ],
            reloadPage: true  // Reload to ensure blocked scripts stop
          }
        },
        marketing: {
          autoClear: {
            cookies: [
              // Google Ads
              { name: /^_gcl/, path: '/', domain: location.hostname },
              { name: /^_gcl/, path: '/', domain: getRootDomain() },
              // DoubleClick
              { name: 'IDE', path: '/', domain: location.hostname },
              { name: 'NID', path: '/', domain: location.hostname },
              // Bing Ads
              { name: /^_uetvid/, path: '/', domain: location.hostname },
              { name: /^_uetvid/, path: '/', domain: getRootDomain() },
              // TikTok (if used for ads)
              { name: '_ttp', path: '/', domain: location.hostname },
              { name: '_ttp', path: '/', domain: getRootDomain() },
              { name: /^_tt/, path: '/', domain: location.hostname },
              { name: /^_tt/, path: '/', domain: getRootDomain() },
              { name: /^ttcsid/, path: '/', domain: location.hostname },
              { name: /^ttcsid/, path: '/', domain: getRootDomain() },
              // Quantcast
              { name: '__qca', path: '/', domain: location.hostname },
              { name: '__qca', path: '/', domain: getRootDomain() },
              // StackAdapt
              { name: /^sa-user-id/, path: '/', domain: location.hostname },
              { name: /^sa-user-id/, path: '/', domain: getRootDomain() },
            ],
            reloadPage: true  // Reload to ensure blocked scripts stop
          }
        }
      },
      language: {
        default: 'en',
        autoDetect: 'browser',
        translations: {
          en: {
            consentModal: {
              title: 'We Use Cookies',
              description: 'We use cookies to enhance your browsing experience, serve personalized  content, and analyze our traffic. By clicking \'Accept All\', you consent to our use of cookies. You can change your preferences in <span class=\'footer-link small cursor-pointer\' id=\'open_preferences_center\'>Cookie Preferences</span>',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
              footer: '<a href=\'/privacy-policy\'>Privacy Policy</a>'
            },
            preferencesModal: {
              title: 'Consent Preferences Center',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close modal',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking \'Accept All\', you consent to our use of cookies.'
                },
                {
                  title: 'Strictly Necessary Cookies <span class=\'pm__badge\'>Always Enabled</span>',
                  description: 'Cookie which are strictly necessary.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Functionality Cookies',
                  description: 'Cookies which are necessary for functionality, including UserWay accessibility settings.',
                  linkedCategory: 'functionality'
                },
                {
                  title: 'Analytics Cookies',
                  description: 'Cookies which are used for analyzing our website traffic.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Advertisement Cookies',
                  description: 'Cookies which are used to advertise our events, products and services.',
                  linkedCategory: 'marketing'
                },
                {
                  title: 'More information',
                  description: 'For any query in relation to my policy on cookies and your choices, please <a class=\'cc__link\' href=\'/market-office?type-privacy\'>contact us</a>.'
                }
              ]
            }
          },
        }
      }
    });

    startUserWayMobilePositionWatcher(this.ngZone);
  }
}
